import React from "react";
import Pillar3D from "./Pillar3D";
import { MEDAL_STYLES } from "./medalStyles";

/**
 * MiniPodium — compact 3-step podium used in the "Top 3 by transaction type"
 * grid.
 *
 * Layout: a header row of 3 avatars + names (visual order 2-1-3) followed by
 * 3 medal-themed 3D pillars carrying the count number.
 */
export default function MiniPodium({ items, accentColor }) {
  const ordered = [items?.[1], items?.[0], items?.[2]];
  const placement = [2, 1, 3];

  return (
    <div className="space-y-3" data-testid="mini-podium">
      {/* Row 1: avatars + names */}
      <div className="grid grid-cols-3 items-end gap-2">
        {ordered.map((p, i) => {
          const rank = placement[i];
          if (!p) {
            return (
              <div
                key={`avatar-empty-${rank}`}
                className="flex flex-col items-center gap-1.5 opacity-40"
                data-testid={`mini-podium-avatar-${rank}-empty`}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full border border-dashed border-border/60 text-xs text-muted-foreground">
                  —
                </div>
                <div className="text-[10px] text-muted-foreground">—</div>
              </div>
            );
          }
          const initial = (p.employee_name || "?").slice(0, 1).toUpperCase();
          return (
            <div
              key={`avatar-${rank}-${p.employee_id}`}
              className="flex flex-col items-center gap-1.5"
              data-testid={`mini-podium-avatar-${rank}`}
            >
              <div
                className="relative flex h-12 w-12 items-center justify-center rounded-full text-sm font-semibold text-white shadow-md"
                style={{
                  background: accentColor,
                  boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
                }}
                title={p.employee_name}
              >
                {initial}
                <span
                  className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full border-2 border-card text-[9px] font-bold text-white"
                  style={{ background: MEDAL_STYLES[rank].side }}
                >
                  {rank}
                </span>
              </div>
              <div
                className="line-clamp-2 w-full whitespace-normal break-words px-0.5 text-center text-xs font-medium leading-tight text-foreground"
                title={p.employee_name}
              >
                {p.employee_name}
              </div>
            </div>
          );
        })}
      </div>

      {/* Row 2: 3D pillars with the count number. */}
      <div className="flex items-end gap-2 pt-2">
        {ordered.map((p, i) => {
          const rank = placement[i];
          const style = MEDAL_STYLES[rank];
          if (!p) {
            return (
              <div
                key={`pillar-empty-${rank}`}
                className={`flex flex-1 flex-col items-center justify-end rounded-t-md border border-dashed border-border/60 ${style.miniHeight} opacity-40`}
                data-testid={`mini-podium-step-${rank}-empty`}
              />
            );
          }
          return (
            <div
              key={`pillar-${rank}-${p.employee_id}`}
              className="flex flex-1 flex-col items-center"
              data-testid={`mini-podium-step-${rank}`}
            >
              <Pillar3D
                style={style}
                depth={8}
                rounded={3}
                className={`w-full ${style.miniHeight} transition-transform duration-300 hover:-translate-y-0.5 hover:scale-[1.05]`}
              >
                <div className="absolute inset-x-0 bottom-1.5 flex justify-center">
                  <span
                    className="text-xl font-bold tabular-nums text-white"
                    style={{ textShadow: "0 1px 2px rgba(0,0,0,0.6)" }}
                  >
                    {p.count.toLocaleString()}
                  </span>
                </div>
              </Pillar3D>
            </div>
          );
        })}
      </div>
    </div>
  );
}
